export interface CaseBlock {
  type: "h2" | "h3" | "p" | "quote" | "list";
  text?: string;
  items?: string[];
}

export interface CaseArticle {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  cover: string;
  date: string;
  readTime: string;
  author: string;
  tags: string[];
  featured?: boolean;
  intro: string;
  content: CaseBlock[];
}

export const caseArticles: CaseArticle[] = [
  {
    slug: "who-are-the-keyboard-warriors",
    title: "键盘侠不是一群陌生人：起底施暴者的三种心理原型",
    category: "施暴者画像",
    excerpt:
      "同一句恶评背后，可能是三种完全不同的人。分清情绪宣泄者、从众跟风者和职业牟利者，才谈得上真正意义上的应对。",
    cover:
      "https://readdy.ai/api/search-image?query=Moody%20dark%20editorial%20abstract%20of%20a%20lone%20faceless%20figure%20surrounded%20by%20floating%20hostile%20comment%20bubbles%2C%20deep%20charcoal%20background%2C%20crimson%20red%20accents%2C%20grainy%20investigative%20magazine%20illustration%2C%20high%20contrast%20warm%20shadows&width=1200&height=800&seq=wbh-c01&orientation=landscape",
    date: "2026-08-18",
    readTime: "9 分钟",
    author: "调查组 · 周砚",
    tags: ["施暴者动机", "键盘侠", "心理原型", "匿名账号"],
    featured: true,
    intro:
      "很多人以为网暴是一群坏人聚在一起作恶，其实评论区里同时站着三种截然不同的人。把他们混为一谈，会让每一次应对都打偏方向。",
    content: [
      { type: "h2", text: "第一种：情绪宣泄者，也是最容易被误解的一群" },
      {
        type: "p",
        text: "他们通常在事件发酵的中段出现，之前并不了解当事人，甚至没有认真看完内容。触发他们的往往是一个足够刺眼的片段，比如一句被截断的发言、一张脱离语境的截图。那一刻他们感受到的是愤怒，而评论是这种愤怒最廉价的出口。",
      },
      {
        type: "p",
        text: "这类人有一个共同特征：攻击强度高，但持续性极差。事件热度过去之后，他们会完全忘记自己说过什么。他们不是职业作恶者，却构成了声量的主体。纯粹的情绪宣泄者数量越多，事件就越容易失控，因为情绪只需要一个方向，不需要事实。",
      },
      {
        type: "quote",
        text: "最喧哗的人往往不是最危险的人，最沉默发力的才值得警惕。",
      },
      { type: "h2", text: "第二种：从众跟风者，负责把声量做成规模" },
      {
        type: "p",
        text: "跟风者的核心动机不是愤怒，而是归属。当评论区已经形成一种明确的情绪立场，加入其中意味着获得认同，退出则可能被视为立场不坚定。于是恶评变成了一种低成本的社交货币。",
      },
      {
        type: "p",
        text: "值得注意的是，跟风者在私下场合往往并不认同自己的发言。他们会说这只是随手一句、只是开玩笑。但他们贡献了网暴最需要的东西：数量。当同一句恶评被复制成千上万次，任何当事人都很难维持正常的心理状态。",
      },
      {
        type: "list",
        items: [
          "行为特征：文案高度相似，多为短句复读，缺少具体论据",
          "时间特征：集中在事件曝光后的 6 到 24 小时之内",
          "持续性：热度消退后迅速消失，几乎不会二次出现",
        ],
      },
      { type: "h2", text: "第三种：职业牟利者，把攻击当成一份工作" },
      {
        type: "p",
        text: "这一类人数量最少，破坏力最大。他们不在意事件本身的对错，关心的是任务是否完成、结款是否到账。发文节奏、文案结构、互动方式都经过设计，甚至会有专门的话术模板来规避平台审核。",
      },
      {
        type: "p",
        text: "判断这一类的关键，往往不在单条内容，而在整体节奏。正常的舆论参与是有起伏的，会随着信息披露而阶段性变化；而职业操作往往呈现出机械的密度：深夜集中发帖、整齐划一的措辞、几乎不参与后续讨论。",
      },
      { type: "h3", text: "为什么要坚持分类" },
      {
        type: "p",
        text: "因为三类人的处理方式完全不同。情绪宣泄者需要的是降温和信息澄清，跟风者需要的是打破从众的心理安全区，而对职业牟利者，只有平台风控和法律手段才真正有效。把所有施暴者都当作同一批人来骂，除了让自己更累，几乎不会改变任何事情。",
      },
      {
        type: "p",
        text: "更重要的是，分类能让人从被迫承受的状态走出来。当你知道对面并不是一支无懈可击的军队，而是一群动机各异的普通人时，恐惧感会明显下降，判断力也会恢复。",
      },
    ],
  },
  {
    slug: "water-army-price-list",
    title: "水军报价单解密：一条差评、一次刷屏，到底卖多少钱",
    category: "水军产业",
    excerpt:
      "从单条评论几毛钱，到包夜刷屏按次计费，网暴为什么总是来势汹汹？因为对某些人来说，它只是一笔可以算清楚的预算。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20stacked%20banknotes%20merging%20with%20smartphone%20screens%20and%20repetitive%20text%20lines%2C%20charcoal%20and%20amber%20palette%2C%20grainy%20editorial%20texture%2C%20investigative%20magazine%20style%2C%20moody%20low%20key&width=1200&height=800&seq=wbh-c02&orientation=landscape",
    date: "2026-08-09",
    readTime: "11 分钟",
    author: "调查组 · 沈柚",
    tags: ["水军", "报价", "接单", "产业链"],
    featured: true,
    intro:
      "当攻击可以被标价，情绪就变成了一种可以被购买的资源。理解这套定价逻辑，才能真正理解网暴为什么总是看起来毫无理性却又异常整齐。",
    content: [
      { type: "h2", text: "价格是怎么算出来的" },
      {
        type: "p",
        text: "公开报道和法院文书里反复出现的一个事实是：绝大多数水军服务的定价，都遵循同一个逻辑——按曝光量、账号等级和风险程度计费。账号越像真人、越不容易被判定为异常，单价就越高。",
      },
      {
        type: "p",
        text: "这也解释了一个常见现象：为什么同一个话题下，有的评论看起来像真人，有的像机器。因为那确实来自不同价位的账号池。低价账号负责冲量，高价账号负责定调。",
      },
      { type: "h3", text: "大致的三档结构" },
      {
        type: "list",
        items: [
          "基础档：批量短评与点赞，主要作用是快速抬高声量，单价最低",
          "进阶段：带立场的长评论与话题引导，需要账号有一定历史痕迹，成本明显上升",
          "定制档：针对特定事件的定向攻击文案，要求规避平台审核，通常按项目整体报价",
        ],
      },
      {
        type: "quote",
        text: "买流量的人和被骂的人之间，隔着的从来不是正义，而是一张预算表。",
      },
      { type: "h2", text: "一条攻击帖的成本，为什么比想象中低" },
      {
        type: "p",
        text: "很多人误以为组织一次有规模的网暴需要很大投入。实际上，由于账号可以批量注册、文案可以模板化、平台审核存在时间差，单次攻击的边际成本非常低。这让网暴从一种偶发的情绪行为，变成了一种可以反复使用的工具。",
      },
      {
        type: "p",
        text: "更麻烦的是，这条链路上的人往往是分离的。花钱的人不直接下场，接单的人只负责分派，执行的人甚至不知道自己在攻击谁。每一环都可以说与自己无关，而伤害却完整地落在当事人身上。",
      },
      { type: "h2", text: "普通人怎么识别这种付费声量" },
      {
        type: "p",
        text: "识别并不需要技术能力，观察几个特征就足够了：评论的措辞是否高度雷同、发布时间是否异常集中、账号资料是否空心化、以及这些账号在此前是否有过正常的社交痕迹。",
      },
      {
        type: "p",
        text: "当你发现一个话题下的批评声几乎只在同一个时间段出现，且内容彼此可以相互替换时，它大概率不是自然形成的舆论。这个判断本身，就能帮你在情绪洪流里保留一点清醒。",
      },
      { type: "h3", text: "对此我们该抱有什么样的心态" },
      {
        type: "p",
        text: "不必因为发现了水军就否定所有批评。真实的批评和购买来的攻击常常混在一起，把两者全部否定，反而会让自己失去改进的机会。更理性的做法是：把可以核实的批评留下，把无法溯源的声量放下。",
      },
    ],
  },
  {
    slug: "organized-bullying-playbook",
    title: "有组织网暴的四步操作手册：从选靶到收割",
    category: "舆论操纵",
    excerpt:
      "选靶、铺场、引爆、收割——一次有组织的攻击并不是失控的情绪，而是一套被反复验证过的流程。看清流程，就不容易被带节奏。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20a%20coordinated%20swarm%20of%20identical%20anonymous%20avatars%20arranged%20in%20rows%20like%20a%20machine%2C%20charcoal%20background%20with%20crimson%20highlights%2C%20grainy%20investigative%20editorial%20art%2C%20cinematic%20contrast&width=1200&height=800&seq=wbh-c03&orientation=landscape",
    date: "2026-07-28",
    readTime: "12 分钟",
    author: "调查组 · 周砚",
    tags: ["舆论操纵", "组织化攻击", "带节奏", "话术"],
    featured: true,
    intro:
      "看起来混乱的网暴，常常是有节奏的。它像一场有脚本的演出，只不过台上的演员不知道自己只是被安排的角色。",
    content: [
      { type: "h2", text: "第一步：选靶，挑选一个足够安全的对象" },
      {
        type: "p",
        text: "被选中的对象通常具备几个共同点：有一定曝光度、缺少强势的舆论资源、以及存在可以被剪裁出争议的片段。攻击者需要的是一个能引起共鸣的对手，而不是一个真正值得辩论的议题。",
      },
      {
        type: "p",
        text: "所以很多网暴的起点并不是某个人做错了什么大事，而是一个可以被无限放大的细节。细节越模糊，可供想象的空间就越大，情绪就越容易被引导。",
      },
      { type: "h2", text: "第二步：铺场，让信息先于当事人到达" },
      {
        type: "p",
        text: "在当事人还在整理事实的时候，铺场已经完成。大量不同角度的片段同时出现，形成一种似乎已经被验证过的印象。这个阶段的目标不是说服，而是制造先入为主。",
      },
      {
        type: "quote",
        text: "舆论的胜负常常不在道理上，而在谁先定义了问题。",
      },
      { type: "h2", text: "第三步：引爆，用情绪替换事实" },
      {
        type: "p",
        text: "点火往往只需要一个高情绪浓度的切口：一段被截断的对话、一张脱离语境的图片、一句被标注了立场的话。此刻事实不再重要，重要的是谁能提供最强烈的情绪。",
      },
      {
        type: "list",
        items: [
          "把复杂问题压缩成一句话，方便复读",
          "把当事人符号化，抹掉具体的处境与背景",
          "给不同意见贴上标签，压缩理性讨论的空间",
        ],
      },
      { type: "h2", text: "第四步：收割，在热度最高处兑现" },
      {
        type: "p",
        text: "收割的形式各不相同。有人收割关注与粉丝，有人收割流量分成，有人只是借此证明自己所在的阵营更强大。真正被消耗的，是当事人的生活、名誉和心理状态。",
      },
      {
        type: "p",
        text: "理解这四步的价值在于：任何一步被打断，整场攻击的成本就会显著上升。所以个人能做的最有效的事，往往是在第二步，也就是在铺场阶段保持沉默，不提供额外的传播节点。",
      },
      { type: "h3", text: "被卷入者的自处方式" },
      {
        type: "p",
        text: "如果你是事件的当事人，尽量做到只发布一次完整说明，之后不再逐条争辩。争辩会成为新的素材，而完整的说明至少能在信息层面留下一个锚点。如果你是旁观者，最好的参与方式是把完整的信息源头找出来，而不是转发最刺激的那一段。",
      },
    ],
  },
  {
    slug: "anonymous-account-economy",
    title: "匿名账号的黑市：批量养号、租号与身份伪装",
    category: "水军产业",
    excerpt:
      "为什么攻击者总是打不完？因为账号本身就是一种可以被批量制造和转卖的商品。看懂账号经济，就看懂了水军为何源源不断。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20a%20marketplace%20of%20blank%20anonymous%20identity%20cards%20and%20disposable%20tokens%2C%20charcoal%20with%20amber%20accents%2C%20grainy%20editorial%20investigative%20texture%2C%20moody&width=1200&height=800&seq=wbh-c04&orientation=landscape",
    date: "2026-07-15",
    readTime: "10 分钟",
    author: "调查组 · 沈柚",
    tags: ["匿名账号", "养号", "账号黑市", "身份伪装"],
    intro:
      "封禁一个账号，对真实用户来说可能意味着失去多年积累，对水军来说却只是增加了一行成本。这种不对称，是网暴难以根治的底层原因之一。",
    content: [
      { type: "h2", text: "账号为什么会变成商品" },
      {
        type: "p",
        text: "平台的信任体系建立在账号的历史行为上。一个注册时间早、日常互动自然、内容多样的账号，天然拥有更高的可信度。而新建的空账号，发出的内容往往会被优先怀疑。",
      },
      {
        type: "p",
        text: "于是账号的历史痕迹本身就有了价值。批量注册之后经过一段时间日常养护、再投入使用的账号，价格会显著高于即时注册的空白账号。这也是为什么很多攻击者看起来不像机器，因为那确实是被人养过一段时间的号。",
      },
      { type: "h3", text: "常见的三种账号来源" },
      {
        type: "list",
        items: [
          "批量注册养护：成本最低，可控性最强，是主流来源",
          "租借真实账号：借用真实用户的历史痕迹，隐蔽性更高",
          "被盗账号：技术上最麻烦，但对攻击者的风险也最大",
        ],
      },
      {
        type: "quote",
        text: "当一个人的身份可以被批量复制，攻击就不再需要勇气，只需要预算。",
      },
      { type: "h2", text: "伪装不止在账号层面" },
      {
        type: "p",
        text: "高级别的操作还会在人格层面做伪装：给账号设置完整的生活轨迹、稳定的兴趣标签、甚至长期维护的社交关系。这样的账号在攻击时极难被识别，也会让旁观者更难相信这是一场组织化的行动。",
      },
      {
        type: "p",
        text: "识别这类账号，需要跳出单条内容，去看整体行为。真实用户的内容是起伏的、有偏好也有情绪的；而伪装账号的内容往往在关键时刻高度一致，平时却异常平淡。",
      },
      { type: "h2", text: "平台治理难在哪里" },
      {
        type: "p",
        text: "平台的治理手段一直在升级，从设备指纹到行为模型，力度并不小。难点在于，治理必须在误伤真实用户和放过组织化操作之间找到平衡。标准太松，水军横行；标准太严，正常用户被误判。",
      },
      {
        type: "p",
        text: "对普通用户来说，能做的其实很有限，但并非没有意义：举报时附上判断依据，而不是只写一句这是水军。把账号列表、时间分布和文案相似度整理清楚，能显著提高被处理的可能性。",
      },
    ],
  },
  {
    slug: "algorithm-amplifies-anger",
    title: "算法如何放大愤怒：平台推荐机制里的网暴温床",
    category: "平台生态",
    excerpt:
      "内容平台偏爱高互动的内容，而愤怒是最容易被互动的一种情绪。当机制奖励冲突，网暴就不只是道德问题，也是产品问题。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20an%20algorithm%20funnel%20turning%20scrolling%20text%20into%20a%20storm%2C%20charcoal%20background%20with%20red%20heat%20glow%2C%20grainy%20editorial%20investigative%20art%2C%20cinematic&width=1200&height=800&seq=wbh-c05&orientation=landscape",
    date: "2026-07-02",
    readTime: "10 分钟",
    author: "调查组 · 何岸",
    tags: ["推荐算法", "热度机制", "平台生态", "信息扩散"],
    intro:
      "如果说水军是主动纵火，算法则更像一阵持续吹向火场的风。它没有恶意，却让每一次零星冲突都有了变成风暴的可能。",
    content: [
      { type: "h2", text: "互动指标为什么偏爱愤怒" },
      {
        type: "p",
        text: "绝大多数内容平台的推荐逻辑，都建立在互动数据之上：点赞、评论、转发、停留时长。在这几项指标里，愤怒的表现几乎是最好的，因为它同时驱动了表达欲和反驳欲。",
      },
      {
        type: "p",
        text: "一条温和理性的内容可能被看完就划走，而一条充满攻击性的内容会引来大量评论，其中相当一部分是反驳。但对算法来说，反驳同样是互动，同样意味着内容值得被更多人看到。",
      },
      { type: "quote",
        text: "算法不理解愤怒，它只理解热度。而这两者常常是同一件事。",
      },
      { type: "h2", text: "从零星冲突到集中爆发" },
      {
        type: "p",
        text: "单条攻击性内容本身不可怕，可怕的是它被推到了原本不关心这件事的人面前。当一个话题突破原有的兴趣圈层，进入泛用户的信息流，参与者的动机就从了解事实变成了表达立场。",
      },
      {
        type: "list",
        items: [
          "第一阶段：内容在小圈层传播，参与者多为知情者",
          "第二阶段：突破圈层，情绪开始脱离具体事实",
          "第三阶段：出现对立阵营，讨论变成站队",
          "第四阶段：内容高度重复，事实补充已无法改变观感",
        ],
      },
      { type: "h2", text: "平台可以做的调整" },
      {
        type: "p",
        text: "一些平台已经开始尝试降低纯情绪型内容的权重、放慢热点话题的扩散速度、以及在争议内容下方优先展示事实性补充。这些调整的共同方向，是让互动指标之外的信号也参与排序。",
      },
      {
        type: "p",
        text: "但机制调整永远滞后于现实。对我们每个人来说，最实际的一点是：你的每一次愤怒互动，都在为这条内容增加推荐权重。不点开、不评论、不转发，本身就是一种有效的应对。",
      },
      { type: "h3", text: "一个容易被忽略的细节" },
      {
        type: "p",
        text: "很多人以为不评论就不算参与，但如果点开并停留了很长时间，同样会被计入互动数据。所以在处理争议内容时，直接划走往往比看完再关掉更有效。",
      },
    ],
  },
  {
    slug: "borrowed-knife-attack",
    title: "借刀杀人：谁在利用网暴达成自己的目的",
    category: "舆论操纵",
    excerpt:
      "最危险的施暴者往往从不亲自出现在评论区。他们只负责提供燃料，然后看着一群愤怒的人替自己完成攻击。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20a%20hand%20puppeteering%20strings%20above%20a%20crowd%20of%20tiny%20faceless%20figures%2C%20charcoal%20background%20with%20amber%20and%20crimson%20light%2C%20grainy%20investigative%20editorial%20art&width=1200&height=800&seq=wbh-c06&orientation=landscape",
    date: "2026-06-20",
    readTime: "9 分钟",
    author: "调查组 · 周砚",
    tags: ["借刀杀人", "幕后推手", "舆论操纵", "利益驱动"],
    intro:
      "直接出面攻击风险太高，于是有人找到了更聪明的方式：点燃一群人的愤怒，让他们替自己出手。事情结束后，他们可以干净地退场。",
    content: [
      { type: "h2", text: "谁有动力借别人的手" },
      {
        type: "p",
        text: "最常见的动机有三类：商业竞争、个人恩怨和流量变现。共同点在于，直接攻击会带来明显的法律与声誉风险，而借舆论之手的成本几乎为零。",
      },
      {
        type: "p",
        text: "操作者通常不会发布完整的指控，而是释放一条半真半假的线索，剩下的留给公众想象。他们深知，由群体自行补全的叙事，比任何直接指控都更有说服力。",
      },
      { type: "h2", text: "为什么这条路总能走通" },
      {
        type: "p",
        text: "因为它精准地利用了公众对不公的敏感。多数人参与讨论的初衷是希望世界更公平一些，这种动机本身是好的，却恰好可以被引向任意一个方向。当情绪被点燃，很少有人会回头追问最初是谁点的火。",
      },
      {
        type: "quote",
        text: "不需要亲自挥刀，只需要确保刀在别人手里。",
      },
      { type: "h2", text: "如何辨认这种操纵" },
      {
        type: "list",
        items: [
          "信息源头模糊，多为截图转述，缺少原始出处",
          "指控指向具体个人，但证据始终停留在暗示层面",
          "话题推进节奏稳定，热度下降时会及时补充新素材",
          "发起者本人从不参与具体讨论，只做情绪性的引导",
        ],
      },
      {
        type: "p",
        text: "如果你发现某个话题始终有人稳定地提供燃料，却从不承担任何论证责任，那么话题背后大概率存在一个明确的操作者。此时最有效的应对，不是反驳细节，而是把注意力拉回源头：这些信息最初是从哪里来的。",
      },
      { type: "h3", text: "给当事人和旁观者的建议" },
      {
        type: "p",
        text: "对当事人来说，攻击的方向可以被引导，但自证清白的过程不该无限继续。把完整的事实陈述一次，然后停止逐条回应，是保护自己最实际的方式。对旁观者来说，追问信息源头，比参与道德审判更有价值。",
      },
    ],
  },
  {
    slug: "where-is-the-line",
    title: "网暴的法律边界：从民事侵权到刑事追责",
    category: "法律边界",
    excerpt:
      "哪些行为只是让人难受，哪些已经越过了法律红线？理解这条边界，不是为了复仇，而是为了在关键时刻知道该往哪里走。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20a%20gavel%20and%20a%20smartphone%20facing%20each%20other%20with%20a%20thin%20dividing%20line%2C%20charcoal%20background%20with%20crimson%20accents%2C%20grainy%20investigative%20editorial%20art%2C%20moody&width=1200&height=800&seq=wbh-c07&orientation=landscape",
    date: "2026-06-08",
    readTime: "13 分钟",
    author: "调查组 · 顾承",
    tags: ["法律边界", "民事侵权", "刑事追责", "证据固定"],
    intro:
      "法律不是让所有恶评都消失的橡皮擦，但它确实划定了一条线。知道线在哪里，能让受害者在混乱中找到可以依靠的东西。",
    content: [
      { type: "h2", text: "民事层面：名誉与人格权益的保护" },
      {
        type: "p",
        text: "侮辱、诽谤、泄露隐私、非法使用个人信息等行为，通常首先构成民事侵权。当事人可以要求停止侵害、删除内容、赔礼道歉并赔偿损失。这类案件的关键不在情绪，而在证据和传播范围的证明。",
      },
      {
        type: "p",
        text: "需要注意的是，民事维权的门槛相对较低，但周期不短。因此在决定走这条路之前，先把证据整理清楚，往往比急于发声明更有帮助。",
      },
      { type: "h2", text: "治安违法：介于两者之间的处理路径" },
      {
        type: "p",
        text: "一部分骚扰、威胁、公开辱骂的行为，可能构成治安管理层面的违法。这类情形通常由公安机关受理，处理速度快于民事诉讼，适用于那些性质明确但损害尚未达到刑事标准的情况。",
      },
      { type: "quote",
        text: "维权的第一步不是喊冤，而是让证据站得住。",
      },
      { type: "h2", text: "刑事层面：什么样的行为会被追责" },
      {
        type: "p",
        text: "当内容明显失实、传播范围广、持续时间长，或造成了严重的实际后果，就可能触及刑法的相关罪名。这类案件的判断较为复杂，通常需要结合具体情节、损害结果和社会影响综合认定。",
      },
      {
        type: "list",
        items: [
          "内容是否可被证明为虚假或明显失实",
          "传播范围与持续时间，是否形成规模化扩散",
          "当事人是否因此遭受严重的精神或经济损失",
          "是否存在组织化操作、有偿发帖等加重情节",
        ],
      },
      { type: "h2", text: "证据固定：越早做越有效的四件事" },
      {
        type: "list",
        items: [
          "完整保存页面链接与发布时间，截图需包含账号信息与转发数据",
          "对动态内容做录屏，避免事后被删除导致证据灭失",
          "整理一份按时间排序的攻击清单，标注内容形式与传播情况",
          "保留因网暴产生的相关凭证，例如就诊记录、误工证明等",
        ],
      },
      {
        type: "p",
        text: "需要强调的是，法律程序和具体认定存在较大的个案差异，以下内容仅作为公开信息的梳理，不构成具体案件的法律意见。一旦涉及实际损害，建议尽早咨询专业律师，由其结合材料判断最合适的路径。",
      },
    ],
  },
  {
    slug: "after-the-storm",
    title: "风暴之后：被网暴者的心理重建与止损清单",
    category: "受害者重建",
    excerpt:
      "网暴最残忍的地方在于，热度会过去，伤害不会自动结束。这篇写给正在风暴里的人和即将走出风暴的人。",
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20abstract%20illustration%20of%20a%20person%20emerging%20from%20a%20storm%20of%20dissolving%20grey%20text%20into%20warm%20light%2C%20charcoal%20to%20amber%20gradient%2C%20grainy%20editorial%20investigative%20art%2C%20hopeful%20moody&width=1200&height=800&seq=wbh-c08&orientation=landscape",
    date: "2026-05-26",
    readTime: "11 分钟",
    author: "调查组 · 苏晚",
    tags: ["心理重建", "止损", "自救", "陪伴"],
    featured: true,
    intro:
      "当热度退去，旁观者很快回到自己的生活，而被攻击的人还在原地。这一篇不谈机制，只谈如何把自己完整地带回来。",
    content: [
      { type: "h2", text: "第一阶段：让人从视野里消失" },
      {
        type: "p",
        text: "风暴最猛烈的阶段，任何一条新消息都可能成为新的燃料。此时最有效的做法不是反击，而是减少暴露：暂时关闭陌生人私信和评论、暂停账号更新、把通知全部静音。",
      },
      {
        type: "p",
        text: "这不是逃避，而是把有限的精力从无法掌控的舆论场上收回来，留给真正需要处理的事情，比如取证、联系律师，或者只是好好睡一觉。",
      },
      { type: "quote",
        text: "你不需要向任何人证明自己没有做错什么，才配得到安静。",
      },
      { type: "h2", text: "第二阶段：把注意力从内容转向证据" },
      {
        type: "p",
        text: "很多当事人在这一阶段会陷入反复阅读恶评的循环。这几乎无法避免，但可以设定边界：把查阅评论的行为集中在一段固定时间内完成，用于整理证据，而不是随时随地被推送牵扯。",
      },
      {
        type: "list",
        items: [
          "安排固定的时间处理与事件相关的信息，其余时间关闭通知",
          "把证据整理交给可信任的人协助，减少自己的重复暴露",
          "只做必要的事实陈述，不参与逐条对辩",
        ],
      },
      { type: "h2", text: "第三阶段：重新建立日常的锚点" },
      {
        type: "p",
        text: "网暴会侵蚀一个人对世界的信任感，让人觉得所有人都带着恶意。此时最重要的事情，是把注意力重新放回具体的、真实的关系上：一顿正常吃下的饭、一次可以好好说话的对话、一个不需要解释太多的朋友。",
      },
      {
        type: "p",
        text: "如果持续的失眠、惊恐、回避社交等症状超过两周仍未缓解，寻求专业心理支持是非常必要的。这不是脆弱的表现，而是对自己负责的方式。",
      },
      { type: "h3", text: "给陪伴者的几句话" },
      {
        type: "p",
        text: "请不要说这点小事不算什么、别看了就好，也不要在当事人面前反复分析他哪里做得不够好。更有效的做法是：承认伤害确实存在、明确表示这件事不是他的错、然后陪着做一些具体而日常的小事。沉默的陪伴，往往比正确的道理更有力量。",
      },
    ],
  },
];

export const caseCategories = [
  "全部",
  "施暴者画像",
  "水军产业",
  "舆论操纵",
  "平台生态",
  "法律边界",
  "受害者重建",
];